import { lazy, Suspense } from "react";
import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider, QueryCache } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { toast } from "sonner";
import i18n from "@/lib/i18n";
import { AuthProvider } from "@/components/auth/AuthProvider";
import { AccountCacheBoundary } from "@/components/auth/AccountCacheBoundary";
import { ErrorBoundary } from "@/components/ui/ErrorBoundary";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";

// Eager: pages users hit on first paint (landing + auth fallback)
import Frontpage from "./pages/Frontpage";
import Auth from "./pages/Auth";
import NotFound from "./pages/NotFound";
import { CookieConsentBanner } from "@/components/legal/CookieConsentBanner";
import { InstallPrompt } from "@/components/pwa/InstallPrompt";
import { MaintenanceGate } from "@/components/MaintenanceGate";
import { NotificationCoordinator } from "@/components/system/NotificationCoordinator";

// Lazy: everything else. Splits the main bundle (~1.7MB) into per-route chunks
// so mobile users on slow networks don't download AICoach + admin upfront.
// Dashboard moved into the lazy set per the perf audit — Frontpage visitors
// no longer pull the Dashboard+OnboardingTour+react-joyride graph (~80 kB gzip
// off the entry chunk).
const Dashboard = lazy(() => import("./pages/Dashboard"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Planner = lazy(() => import("./pages/Planner"));
const Library = lazy(() => import("./pages/Library"));
const Marketplace = lazy(() => import("./pages/Marketplace"));
const Profile = lazy(() => import("./pages/Profile"));
const Progress = lazy(() => import("./pages/Progress"));
const History = lazy(() => import("./pages/History"));
const Exercises = lazy(() => import("./pages/Exercises"));
const ExerciseDetail = lazy(() => import("./pages/ExerciseDetail"));
const SessionExecution = lazy(() => import("./pages/SessionExecution"));
const SessionSummary = lazy(() => import("./pages/SessionSummary"));
const AICoach = lazy(() => import("./pages/AICoach"));
const Wallet = lazy(() => import("./pages/Wallet"));
const Nutrition = lazy(() => import("./pages/Nutrition"));
const SubscriptionSuccess = lazy(() => import("./pages/SubscriptionSuccess"));
const SubscriptionCancel = lazy(() => import("./pages/SubscriptionCancel"));
const Upgrade = lazy(() => import("./pages/Upgrade"));
const DesignLab = lazy(() => import("./pages/DesignLab"));
const Feedback = lazy(() => import("./pages/Feedback"));
const Social = lazy(() => import("./pages/Social"));
const Import = lazy(() => import("./pages/Import"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Help = lazy(() => import("./pages/Help"));
const PublicWorkout = lazy(() => import("./pages/PublicWorkout"));

// Admin pages — own chunk per page, almost no end-users hit these
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminUsers = lazy(() => import("./pages/admin/Users"));
const AdminAssets = lazy(() => import("./pages/admin/Assets"));
const AdminMedia = lazy(() => import("./pages/admin/Media"));
const AdminExercises = lazy(() => import("./pages/admin/Exercises"));
const AdminWorkouts = lazy(() => import("./pages/admin/Workouts"));
const AdminMarketplace = lazy(() => import("./pages/admin/Marketplace"));
const AdminEconomy = lazy(() => import("./pages/admin/Economy"));
const AdminChallenges = lazy(() => import("./pages/admin/Challenges"));
const AdminSystem = lazy(() => import("./pages/admin/System"));
const AdminCreators = lazy(() => import("./pages/admin/Creators"));
const AdminIntegrity = lazy(() => import("./pages/admin/Integrity"));
const AdminInsights = lazy(() => import("./pages/admin/Insights"));
const AdminSettings = lazy(() => import("./pages/admin/Settings"));
const AdminUpgradePage = lazy(() => import("./pages/admin/UpgradePage"));

// Global query error feedback. Individual pages don't all surface
// useQuery errors, so a Supabase outage could leave a page silently
// blank. The QueryCache.onError fires once per failed query: we toast
// only when there's no cached data to fall back on (an empty screen the
// user would otherwise stare at), and throttle so a burst of parallel
// queries failing together shows a single message, not ten.
let lastQueryErrorToast = 0;
const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (_error, query) => {
      if (query.meta?.suppressGlobalErrorToast === true) return;
      if (query.state.data !== undefined) return; // stale data still shown
      const now = Date.now();
      if (now - lastQueryErrorToast < 4000) return;
      lastQueryErrorToast = now;
      toast.error(i18n.t("common.load_error"));
    },
  }),
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 30_000,
      // Mobile/PWA: avoid a refetch storm every time the app regains
      // focus (tab switch, returning from background).
      refetchOnWindowFocus: false,
    },
  },
});

const RouteFallback = () => (
  <div className="min-h-screen grid place-items-center">
    <LoadingSpinner />
  </div>
);

const App = () => (
  <ErrorBoundary>
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <AccountCacheBoundary>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <NotificationCoordinator>
                <CookieConsentBanner />
                <InstallPrompt />
              </NotificationCoordinator>
              <MaintenanceGate>
                <Suspense fallback={<RouteFallback />}>
                  <Routes>
                  {/* Public routes */}
                  <Route path="/" element={<Frontpage />} />
                  <Route path="/welcome" element={<Navigate to="/" replace />} />
                  <Route path="/login" element={<Auth />} />
                  <Route path="/signup" element={<Auth />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/w/:slug" element={<PublicWorkout />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/privacy" element={<Privacy />} />

                  {/* App routes (auth required) */}
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/planner" element={<Planner />} />
                  <Route path="/library" element={<Library />} />
                  <Route path="/marketplace" element={<Marketplace />} />
                  <Route path="/marketplace/programs/:programId" element={<Marketplace />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/progress" element={<Progress />} />
                  <Route path="/history" element={<History />} />
                  <Route path="/exercises" element={<Exercises />} />
                  <Route path="/exercises/:exerciseId/stats" element={<ExerciseDetail />} />
                  <Route path="/wallet" element={<Wallet />} />
                  <Route path="/session/:sessionId" element={<SessionExecution />} />
                  <Route path="/session/:sessionId/summary" element={<SessionSummary />} />
                  <Route path="/ai-coach" element={<AICoach />} />
                  <Route path="/nutrition" element={<Nutrition />} />
                  <Route path="/subscription/success" element={<SubscriptionSuccess />} />
                  <Route path="/subscription/cancel" element={<SubscriptionCancel />} />
                  <Route path="/upgrade" element={<Upgrade />} />
                  <Route path="/design-lab" element={<DesignLab />} />
                  <Route path="/feedback" element={<Feedback />} />
                  <Route path="/social" element={<Social />} />
                  <Route path="/import" element={<Import />} />
                  <Route path="/help" element={<Help />} />

                  {/* Admin routes */}
                  <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
                  <Route path="/admin/dashboard" element={<AdminDashboard />} />
                  <Route path="/admin/users" element={<AdminUsers />} />
                  <Route path="/admin/assets" element={<AdminAssets />} />
                  <Route path="/admin/media" element={<AdminMedia />} />
                  <Route path="/admin/exercises" element={<AdminExercises />} />
                  <Route path="/admin/workouts" element={<AdminWorkouts />} />
                  <Route path="/admin/marketplace" element={<AdminMarketplace />} />
                  <Route path="/admin/creators" element={<AdminCreators />} />
                  <Route path="/admin/economy" element={<AdminEconomy />} />
                  <Route path="/admin/challenges" element={<AdminChallenges />} />
                  <Route path="/admin/system" element={<AdminSystem />} />
                  <Route path="/admin/integrity" element={<AdminIntegrity />} />
                  <Route path="/admin/insights" element={<AdminInsights />} />
                  <Route path="/admin/settings" element={<AdminSettings />} />
                  <Route path="/admin/upgrade-page" element={<AdminUpgradePage />} />

                  {/* Catch-all */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
                </Suspense>
              </MaintenanceGate>
            </BrowserRouter>
          </TooltipProvider>
          </AccountCacheBoundary>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </ErrorBoundary>
);

export default App;
